import { ActionItemArchivedMessage } from "components/MessageBubble/ActionItemArchivedMessage";
import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { useModal } from "hooks/useModal";
import dayjs from "dayjs";
import { DISPLAY_DATE_HH_MM_A } from "constants/date";
import { ReactNode } from "react";
import { renderText } from "stream-chat-react";

type MeetingRequestChatCardProps = {
  customData: {
    header: string;
    description: string | null;
    card_status: "REQUESTED" | "SUBMITTED";
    metadata: {
      team_display: string;
      calendly_link: string;
      meeting_purpose: string | null;
      scheduled_at: string | null;
    };
  };
  size?: "regular" | "block";
  isDeprecated: boolean;
};

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="t-flex t-flex-col t-gap-1">
    <span className="t-text-body-sm t-text-text-30 t-font-medium">{label}</span>
    {children}
  </div>
);

const MeetingRequestChatCard = ({
  customData,
  size,
  isDeprecated,
}: MeetingRequestChatCardProps) => {
  const detailsModal = useModal();

  const {
    header,
    description,
    card_status,
    metadata: { team_display, calendly_link, meeting_purpose, scheduled_at },
  } = customData;

  const isRequested = card_status === "REQUESTED";

  const openCalendly = () => {
    window.open(calendly_link, "_blank", "noopener,noreferrer");
  };

  const cta = isDeprecated ? (
    <ActionItemArchivedMessage />
  ) : isRequested ? (
    <Button customType="primary" size="small" block onClick={openCalendly}>
      Schedule Meeting
    </Button>
  ) : (
    <Button
      customType="secondary"
      size="small"
      block
      onClick={detailsModal.open}
    >
      View Details
    </Button>
  );

  return (
    <>
      <ChatCard.Root size={size}>
        <ChatCard.Header>
          <div className="t-flex t-items-center t-gap-1.5">
            {isRequested ? (
              <CalendarBlank />
            ) : (
              <CheckCircleFilled className="t-text-dark_green-50" />
            )}
            <div>{isRequested ? header : "Meeting Scheduled"}</div>
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-text-body-sm t-font-light t-text-text-30">
            Meeting with {team_display} Team
          </div>
          {cta}
        </ChatCard.Body>
      </ChatCard.Root>

      {isRequested && description && (
        <div className="t-m-0 t-text-body-sm t-text-text-60 [&_p]:t-whitespace-pre-line [&_p]:t-m-0">
          {renderText(description)}
        </div>
      )}

      {!isRequested && (
        <Modal.Root
          open={detailsModal.isOpen}
          onOpenChange={detailsModal.close}
        >
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Meeting Details</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <div className="t-flex t-flex-col t-gap-4">
                {scheduled_at && (
                  <DetailRow label="Date & Time">
                    <div className="t-flex t-items-center t-gap-1.5">
                      <CalendarBlank width={16} height={16} />
                      <span className="t-text-body-sm t-text-text-100">
                        {dayjs(scheduled_at).format(DISPLAY_DATE_HH_MM_A)}
                      </span>
                    </div>
                  </DetailRow>
                )}
                <DetailRow label="Team">
                  <div className="t-flex t-items-center t-gap-1.5">
                    <span className="t-text-body-sm t-text-text-100">
                      {team_display} Team
                    </span>
                    <Tag tagType="green" size="small" icon={false}>
                      Scheduled
                    </Tag>
                  </div>
                </DetailRow>
                {meeting_purpose && (
                  <DetailRow label="Purpose">
                    <span className="t-text-body-sm t-text-text-60">
                      {meeting_purpose}
                    </span>
                  </DetailRow>
                )}
                <DetailRow label="Calendly Link">
                  <a
                    href={calendly_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="t-text-purple t-text-body-sm t-break-all"
                  >
                    {calendly_link}
                  </a>
                </DetailRow>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button>Close</Button>
              </Modal.RawClose>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>
      )}
    </>
  );
};

export default MeetingRequestChatCard;
